import { useEffect, useId, useMemo, useState } from "react";
import type IJitsiMeetExternalApi from "@jitsi/react-sdk/lib/types/IJitsiMeetExternalApi";
import { Ban, Search, ShieldCheck, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/primitives/button";
import { Input } from "@/components/primitives/input";
import { Switch } from "@/components/primitives/switch";
import { cn } from "@/lib/utils";
import showDialog from "@/utils/showDialog";
import { useCallState } from "@/zustand/CallState";
import { useCallModerationState } from "@/zustand/CallModerationState";
import BreakoutRoomsSection from "./BreakoutRoomsSection";
import ModerationParticipantRow from "./ModerationParticipantRow";

type Props = {
  onClose: () => void;
  /**
   * Visitor/webinar tier is Phase 5 and is NOT built yet. Defaults to `false` so every existing
   * (non-webinar) call is provably unaffected by this flag. See the reactions note below.
   */
  isWebinarMode?: boolean;
  /**
   * Explicit external-API override for embeds that do NOT own `CallState`
   * (the standalone meet page, `MeetJoinPage`). When provided, every command
   * targets THIS api instead of the store's `apiRef`, so the standalone
   * surface never has to write into the in-chat call's global state.
   * Omitted by `Call.tsx` — the store read below keeps its behavior unchanged.
   */
  apiOverride?: IJitsiMeetExternalApi | null;
};

/**
 * Slide-in moderation drawer, rendered inside `Call.tsx` above the Jitsi iframe.
 *
 * Self-gates on `localIsModerator` (design decision #1: today's moderator rule is unchanged —
 * the panel trusts whatever the iframe itself reports the local participant's role as). This
 * check lives here, not just in the caller, so the panel can never render for a non-moderator
 * regardless of how it's mounted.
 *
 * VISUAL LANGUAGE (Orion reskin): a backdrop scrim + slide-in drawer in the same family as
 * `OrionChatPanel` — DM Sans, `border-primary/40` edge, sectioned body (approval controls →
 * participant roster → an isolated "danger zone" footer for the irreversible end-call action).
 * The panel's FUNCTION and every `apiRef` command are unchanged; only the presentation is.
 */
export default function ModerationPanel({
  onClose,
  isWebinarMode = false,
  apiOverride,
}: Props) {
  const { t } = useTranslation();
  const storeApiRef = useCallState((state) => state.apiRef);
  // `undefined` = "no override supplied" (in-chat call) — fall back to the store.
  // An explicit `null` override means "no api yet" and must NOT fall through to
  // a possibly-stale in-chat apiRef.
  const apiRef = apiOverride === undefined ? storeApiRef : apiOverride;
  const localIsModerator = useCallModerationState(
    (state) => state.localIsModerator,
  );
  const participants = useCallModerationState((state) => state.participants);
  const moderationOn = useCallModerationState((state) => state.moderationOn);

  // Case-insensitive substring filter over the roster. For webinar-scale calls (100s of
  // participants) this both narrows what a moderator has to scan AND cuts the number of rows
  // React actually renders while a query is active. The roster `<ul>` is NOT virtualized — a
  // 100s-row unfiltered list still renders every row, so heavy webinars will want virtualization
  // as a separate follow-up; the filter alone is the accepted first step (see PR notes).
  const [search, setSearch] = useState("");
  const searchInputId = useId();

  // Only remote participants get action rows — muting/kicking/granting to yourself isn't a
  // meaningful action from this panel (your own mic/camera controls live in the call toolbar).
  const remoteParticipants = useMemo(
    () => participants.filter((p) => !p.isLocal),
    [participants],
  );

  // Filter the DISPLAYED roster only — never mutate the store (the search box is pure view state).
  // Empty/whitespace-only query shows everyone.
  const query = search.trim().toLowerCase();
  const filteredParticipants = useMemo(
    () =>
      query === ""
        ? remoteParticipants
        : remoteParticipants.filter((p) =>
            p.displayName.toLowerCase().includes(query),
          ),
    [remoteParticipants, query],
  );

  // Escape closes the drawer — standard for a `role="dialog"`. Gated on `localIsModerator` so the
  // listener is only live while the panel is actually visible.
  useEffect(() => {
    if (!localIsModerator) {
      return;
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [localIsModerator, onClose]);

  if (!localIsModerator) {
    return null;
  }

  function handleToggleModeration(
    mediaType: "audio" | "video",
    enabled: boolean,
  ) {
    // camelCase command KEY (verified against external_api.js v=9268: `toggleModeration` →
    // "toggle-moderation"). The hyphenated wire-name is NOT accepted by executeCommand.
    // ARG ORDER: Jitsi's handler is (enabled, mediaType) — see upstream API.js:477. It was
    // previously called as (mediaType, enabled), which read the "audio"/"video" string as the
    // `enabled` flag (always truthy → always ON) and the boolean as `mediaType` (never
    // === "video" → always the audio branch). So both switches enabled audio moderation and
    // OFF never disabled anything. Correct order is (enabled, mediaType).
    apiRef?.executeCommand("toggleModeration", enabled, mediaType);
  }

  async function handleEndCallForAll() {
    // Destructive + irreversible (ends the meeting for everyone) — confirm before firing.
    const confirmed = await showDialog(
      t("videoCall.moderation.endCallForAllTitle"),
      t("videoCall.moderation.endCallForAllConfirm"),
      t("videoCall.moderation.endCallForAll"),
    );
    if (!confirmed) {
      return;
    }
    // camelCase command KEY: `endConference` → "end-conference" (external_api.js v=9268). This
    // ends the meeting for EVERYONE — distinct from `hangup` (→ "video-hangup"), which only makes
    // the local participant leave. The hyphenated "end-conference" is NOT accepted by
    // executeCommand and was silently failing with "Not supported command name."
    apiRef?.executeCommand("endConference");
  }

  return (
    <>
      {/* Backdrop scrim — dims the call behind the drawer and closes it on click. */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 z-[55] bg-black/50 duration-200 animate-in fade-in"
      />

      <div
        role="dialog"
        aria-label={t("videoCall.moderation.panelTitle")}
        className={cn(
          "absolute inset-y-0 right-0 z-[60] flex w-full max-w-sm flex-col overflow-hidden",
          "border-l border-primary/40 bg-background font-DMSans text-foreground shadow-xl",
          "duration-200 animate-in slide-in-from-right",
          "sm:w-96 sm:max-w-none md:w-[400px]",
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 border-b border-foreground/10 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <ShieldCheck className="size-4 shrink-0 text-primary" />
            <h2 className="truncate text-sm font-semibold tracking-tight">
              {t("videoCall.moderation.panelTitle")}
            </h2>
          </div>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="size-8"
            aria-label={t("videoCall.moderation.close")}
            onClick={onClose}
          >
            <X className="size-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Approval controls — routine, non-destructive switches. */}
          <section className="flex flex-col gap-1 border-b border-foreground/10 p-3">
            <h3 className="px-1 pb-1 text-xs font-semibold uppercase tracking-wider text-foreground/50">
              {t("videoCall.moderation.controlsHeading")}
            </h3>

            <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md px-1 py-2 text-sm transition-colors hover:bg-foreground/5">
              <span>{t("videoCall.moderation.requireApprovalAudio")}</span>
              <Switch
                checked={moderationOn.audio}
                onCheckedChange={(checked) =>
                  handleToggleModeration("audio", checked)
                }
                aria-label={t("videoCall.moderation.requireApprovalAudio")}
              />
            </label>

            <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md px-1 py-2 text-sm transition-colors hover:bg-foreground/5">
              <span>{t("videoCall.moderation.requireApprovalVideo")}</span>
              <Switch
                checked={moderationOn.video}
                onCheckedChange={(checked) =>
                  handleToggleModeration("video", checked)
                }
                aria-label={t("videoCall.moderation.requireApprovalVideo")}
              />
            </label>

            {isWebinarMode && (
              // TODO(webinar-reactions — design doc §5): the visitor/webinar tier isn't built yet.
              // When it ships, reactions are disabled via the `disableReactions` (and
              // `disableReactionsModeration`) config flag — passed through `configOverwrite` at iframe
              // construction time, or the `overwrite-config` command at runtime — NOT via any per-call
              // executeCommand (no `toggle-reactions`-style command exists on stable/jitsi-meet_11031).
              // This is currently flag plumbing + a visible note only; no reaction enforcement is
              // wired up yet.
              <div
                data-testid="webinar-reactions-note"
                className={cn(
                  "mt-1 flex items-center gap-2 rounded-md bg-foreground/5 p-2 text-xs text-foreground/70",
                )}
              >
                <Ban className="size-4 shrink-0" />
                <span>
                  {t("videoCall.moderation.reactionsDisabledWebinar")}
                </span>
              </div>
            )}
          </section>

          {/* Participant roster */}
          <section className="p-2">
            <h3 className="px-2 py-1 text-xs font-semibold uppercase tracking-wider text-foreground/50">
              {t("videoCall.moderation.participantsHeading")}
            </h3>
            {remoteParticipants.length === 0 ? (
              <p className="px-2 py-3 text-sm text-foreground/50">
                {t("videoCall.moderation.noParticipants")}
              </p>
            ) : (
              <>
                {/* Roster search — only shown when there's a roster to filter. Labelled for a11y
                    (a visually-hidden <label> tied to the input) so screen readers announce it. */}
                <div className="px-1 pb-2">
                  <label htmlFor={searchInputId} className="sr-only">
                    {t("videoCall.moderation.searchLabel")}
                  </label>
                  <div className="relative">
                    <Search
                      className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-foreground/40"
                      aria-hidden="true"
                    />
                    <Input
                      id={searchInputId}
                      type="text"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder={t("videoCall.moderation.searchPlaceholder")}
                      className="pl-8"
                    />
                  </div>
                </div>
                {filteredParticipants.length === 0 ? (
                  <p className="px-2 py-3 text-sm text-foreground/50">
                    {t("videoCall.moderation.noMatches")}
                  </p>
                ) : (
                  <ul className="flex flex-col gap-1">
                    {filteredParticipants.map((participant) => (
                      <ModerationParticipantRow
                        key={participant.id}
                        participant={participant}
                        apiRef={apiRef}
                      />
                    ))}
                  </ul>
                )}
              </>
            )}
          </section>

          {/* Breakout rooms — the same single-roster surface hosts breakout controls rather than
              re-enabling Jitsi's native participants-pane (design doc §2). Moderator-only by virtue
              of living inside this panel, which renders nothing for a non-moderator. */}
          <BreakoutRoomsSection apiRef={apiRef} />
        </div>

        {/* Danger zone — the one irreversible action, isolated from the routine controls. */}
        <div className="border-t border-destructive/30 bg-destructive/5 p-3">
          <h3 className="px-1 pb-2 text-xs font-semibold uppercase tracking-wider text-destructive/80">
            {t("videoCall.moderation.dangerZoneHeading")}
          </h3>
          <Button
            variant="destructive"
            size="sm"
            className="w-full"
            onClick={handleEndCallForAll}
          >
            {t("videoCall.moderation.endCallForAll")}
          </Button>
        </div>
      </div>
    </>
  );
}
